import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import { getCohortBuilderPath, getProjectCohortVersionPath } from '@verantos-dev/core-constants';
import { CohortBuilderPageTab } from '@verantos-dev/core-enums';
import {
  cohortQueryKeys,
  fetchCohortVersion,
  useCreateCohortMutation,
  useCreateCohortProfileMutation,
} from '@verantos-dev/data-access-cohorts';
import { useGetProjectQuery } from '@verantos-dev/data-access-projects';
import { ApiWorkflowDialogMessages } from '@verantos-dev/shared-components';
import { type CreateCohortWithDatasetData } from '@verantos-dev/types-cohorts';

import { useCohortModalContext } from '../context/modalContext';

export const useCreateCohortModal = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { projectId } = useParams<{ projectId: string }>();
  // TODO: cleanup with research_assistant_gen_3 ff — once gen_3 is the
  // default, drop the legacy `getCohortBuilderPath` branch below.
  const isGen3Enabled = useFeatureFlagSplit('research_assistant_gen_3', false) as boolean;

  // local state for model entry
  const [cohortName, setCohortName] = useState('');

  // context for modal state
  const { closeDialog } = useCohortModalContext();

  // projects query hook
  const { data: _data, error: _error } = useGetProjectQuery(projectId || '');

  // Cohort creation mutation hook. Gen-3 uses the keyed TanStack mutation so
  // the project-page artifact manifest + activity feed refresh on create
  // (OR-6111); legacy keeps the RTK mutation untouched. Both hooks are called
  // unconditionally (rules of hooks); only the selected result is used.
  // TODO: cleanup with research_assistant_gen_3 ff — drop the RTK branch.
  const [rtkCreateCohort, rtkCreateState] = useCreateCohortMutation();
  const gen3CreateCohort = useCreateCohortProfileMutation();

  const isCreateCohortLoading = isGen3Enabled
    ? gen3CreateCohort.isPending
    : rtkCreateState.isLoading;
  const createCohortResponse = isGen3Enabled ? gen3CreateCohort.data : rtkCreateState.data;
  // TanStack surfaces `error` as `Error | null`; the dialog's `submittingError`
  // prop expects `... | undefined`, so coerce null away on the gen-3 branch.
  const createCohortError = isGen3Enabled
    ? gen3CreateCohort.error ?? undefined
    : rtkCreateState.error;

  // handle cohort name change
  const onChangeCohortName = (_: string, value: string) => {
    setCohortName(value);
  };

  // handle form submission
  const onSubmit = async () => {
    const cohortNameTrimmed = cohortName?.trim();
    if (!cohortNameTrimmed) {
      return;
    }

    try {
      const createCohortData: CreateCohortWithDatasetData = {
        cohortName: cohortNameTrimmed,
        cohortDescription: '',
        datasetReleaseId: _data?.data.datasetReleaseId || '',
      };
      const createArgs = { projectId: projectId || '', data: createCohortData };
      if (isGen3Enabled) {
        await gen3CreateCohort.mutateAsync(createArgs);
      } else {
        await rtkCreateCohort(createArgs);
      }
    } catch (error) {
      console.error('Error creating cohort', error);
    }
  };

  // handle redirect after successful
  const onRedirectAfterSubmit = () => {
    const cohortId = createCohortResponse?.data?.cohortProfileId;
    const cohortVersionId = createCohortResponse?.data?.latestCohortVersionId;
    if (projectId && cohortId && cohortVersionId) {
      // Prime the TanStack cache with the cohort version we just created so
      // the builder page render is warm on first paint. Fire-and-forget —
      // mirrors the old RTK `initiate(...)` behaviour.
      void queryClient.prefetchQuery({
        queryKey: cohortQueryKeys.version(cohortVersionId),
        queryFn: () => fetchCohortVersion(projectId, cohortId, cohortVersionId),
      });
      const destinationBase = isGen3Enabled
        ? getProjectCohortVersionPath(projectId, cohortId, cohortVersionId)
        : getCohortBuilderPath(projectId, cohortId, cohortVersionId);
      const destinationTab = isGen3Enabled
        ? CohortBuilderPageTab.definition
        : CohortBuilderPageTab.events;
      navigate(`${destinationBase}?pageTab=${destinationTab}`);
    }
  };

  // messages for the modal content area
  const createCohortModalContentMessages = {
    cohortNameLabel: intl.formatMessage({
      id: 'cohortsList.createCohortModal.cohortName.label',
    }),
    cohortNamePlaceholder: intl.formatMessage({
      id: 'cohortsList.createCohortModal.cohortName.placeholder',
    }),
    datasetLabel: intl.formatMessage({ id: 'cohortsList.createCohortModal.dataset.label' }),
    datasetPlaceholder: intl.formatMessage({
      id: 'cohortsList.createCohortModal.dataset.placeholder',
    }),
    maxCharacterErrorMsg: intl.formatMessage({
      id: 'cohortsList.createCohortModal.maxCharacterErrorMsg',
    }),
  };

  // message overrides for the api workflow dialog
  const apiWorkflowDialogMessages: ApiWorkflowDialogMessages = {
    loadingCancelButtonText: intl.formatMessage({
      id: 'cohortsList.createCohortModal.cancelButton',
    }),
    loadingSaveButtonText: intl.formatMessage({ id: 'cohortsList.createCohortModal.saveButton' }),

    entryCancelButtonText: intl.formatMessage({
      id: 'cohortsList.createCohortModal.cancelButton',
    }),
    entrySaveButtonText: intl.formatMessage({ id: 'cohortsList.createCohortModal.saveButton' }),

    submittingCancelButtonText: intl.formatMessage({
      id: 'cohortsList.createCohortModal.cancelButton',
    }),
    submittingSaveButtonText: intl.formatMessage({
      id: 'cohortsList.createCohortModal.submitting',
    }),

    successCloseButtonText: intl.formatMessage({
      id: 'cohortsList.createCohortModal.closeButton',
    }),

    submittingErrorTitle: intl.formatMessage({
      id: 'cohortsList.createCohortModal.submittingError',
    }),

    successTitle: intl.formatMessage({ id: 'cohortsList.createCohortModal.success.title' }),
    successText: intl.formatMessage(
      { id: 'cohortsList.createCohortModal.success.text' },
      { cohortName: cohortName }
    ),
  };

  return {
    title: intl.formatMessage({ id: 'cohortsList.createCohortModal.title' }),
    closeCreateCohortModal: closeDialog,
    createCohortModalContentMessages,
    apiWorkflowDialogMessages,
    _error,
    releases: _data?.data,
    cohortName,
    onChangeCohortName,
    onSubmit,
    onRedirectAfterSubmit,
    isCreateCohortLoading,
    createCohortError,
    isValidToSubmit: Boolean(cohortName?.trim()),
  };
};
